import React, { Component, ErrorInfo, ReactNode } from 'react';
import { NonIdealState, Button, Intent, Collapse, Card, Elevation } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error, 
      errorInfo: null,
      showDetails: false 
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('❌ Error caught by ErrorBoundary:', error);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    this.setState({
      error,
      errorInfo
    });

    // Log to external service if needed
    if (typeof window !== 'undefined' && (window as any).analyticsService) {
      (window as any).analyticsService.trackError(error, {
        componentStack: errorInfo.componentStack,
        props: (this.props as any)
      });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      
      return (
        <div style={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: '#f5f8fa'
        }}>
          <Card elevation={Elevation.THREE} style={{ maxWidth: '600px', width: '100%' }}>
            <NonIdealState
              icon={IconNames.ERROR}
              iconSize={48}
              title="Application Error"
              description={
                <div>
                  <p style={{ marginBottom: '15px' }}>
                    An unexpected error has occurred. The application cannot continue.
                  </p>
                  {error && (
                    <p style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '12px',
                      color: '#d73a49',
                      marginBottom: '15px'
                    }}>
                      {error.toString()}
                    </p>
                  )}
                </div>
              }
              action={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <Button 
                      intent={Intent.PRIMARY}
                      icon={IconNames.REFRESH}
                      onClick={this.handleReload}
                    >
                      Reload Application
                    </Button>
                    <Button
                      icon={showDetails ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
                      onClick={this.toggleDetails}
                    >
                      {showDetails ? 'Hide' : 'Show'} Details
                    </Button>
                  </div>
                  
                  <Collapse isOpen={showDetails}>
                    <Card 
                      elevation={Elevation.ONE}
                      style={{ 
                        marginTop: '15px',
                        backgroundColor: '#2b2b2b',
                        color: '#f5f5f5',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }}
                    >
                      <div style={{ padding: '15px' }}>
                        <div style={{ marginBottom: '15px' }}>
                          <strong style={{ color: '#ff6b6b' }}>Error Stack:</strong>
                          <pre style={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            marginTop: '5px' 
                          }}>
                            {error?.stack || 'No stack trace available'}
                          </pre>
                        </div>
                        
                        {errorInfo && (
                          <div>
                            <strong style={{ color: '#ff6b6b' }}>Component Stack:</strong>
                            <pre style={{ 
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              marginTop: '5px'
                            }}>
                              {errorInfo.componentStack}
                            </pre>
                          </div>
                        )}
                      </div>
                    </Card>
                  </Collapse>
                </div>
              }
            />
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 